import { Alert } from "@/deps/ui-library";
import { useVatReturn } from "@/api/queries";
import { formatDate } from "@/lib/dates";
import {
  Figure,
  MonthField,
  StatementTable,
  monthLabel,
  useMonthParam,
  type StatementRow,
} from "./reportParts";
import { ReportFrame } from "./ReportFrame";
import { useTaxWords } from "@/app/session";

const count = (n: number) => `${n} ${n === 1 ? "document" : "documents"}`;

/** A month's VAT, from the documents entered in it. Not a filing (ADR-0027). */
export function VatReturnPage() {
  const say = useTaxWords();
  const { month, months, setMonth, from, to } = useMonthParam("/reports/vat-return");
  const { data, isPending, error } = useVatReturn(from, to);
  const period = `${formatDate(from)} – ${formatDate(to)}`;

  const outputRows: StatementRow[] = (data?.output ?? []).map((row) => ({
    label: `${row.category}${row.ratePct ? ` · ${row.ratePct}%` : ""} · ${count(row.documents)}`,
    kind: "line",
    values: [row.taxable, row.tax],
  }));
  const inputRows: StatementRow[] = (data?.input ?? []).map((row) => ({
    label: `${row.recoverable ? "Recoverable" : "Non-recoverable, stays in the expense"} · ${row.ratePct}% · ${count(row.documents)}`,
    kind: "line",
    values: [row.taxable, row.recoverable ? row.tax : null],
  }));

  const payable = Number(data?.payable ?? 0);
  const payLabel = payable >= 0 ? say("VAT payable") : "Credit carried forward";
  const summary: StatementRow[] = data
    ? [
        { label: say("Output VAT on sales"), kind: "line", values: [data.outputTax] },
        {
          label: say("Less: recoverable input VAT"),
          kind: "line",
          values: [data.recoverableInputTax],
        },
        { label: "Less: credit carried in", kind: "line", values: [data.creditCarried] },
        { label: payLabel, kind: "total", values: [Math.abs(payable).toFixed(2)] },
      ]
    : [];

  return (
    <ReportFrame
      id="vat"
      title={say("VAT return")}
      period={period}
      exportable={
        data
          ? () => ({
              title: say("VAT return"),
              period,
              columns: [
                { header: "" },
                { header: "Taxable value", numeric: true },
                { header: say("VAT"), numeric: true },
              ],
              rows: [
                ...outputRows.map((row) => [`Output · ${row.label}`, ...row.values]),
                ...inputRows.map((row) => [`Input · ${row.label}`, ...row.values]),
                ...summary.map((row) => [row.label, null, ...row.values]),
              ],
            })
          : undefined
      }
    >
      <div className="flex flex-wrap items-end gap-2">
        <MonthField month={month} months={months} onChange={setMonth} />
      </div>

      {error ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={error instanceof Error ? error.message : "That report could not be loaded."}
        />
      ) : null}

      {data ? (
        <div className="flex flex-wrap gap-3">
          <Figure label={say("Output VAT")} value={data.outputTax} />
          <Figure label={say("Recoverable input VAT")} value={data.recoverableInputTax} />
          <Figure
            label={payLabel}
            value={Math.abs(payable).toFixed(2)}
            detail={monthLabel(month)}
          />
        </div>
      ) : null}

      <StatementTable
        caption={say("Output VAT on sales")}
        columns={[say("Output VAT · sales"), "Taxable value", say("VAT")]}
        rows={outputRows}
        loading={isPending}
      />
      <StatementTable
        caption={say("Input VAT on purchases")}
        columns={[say("Input VAT · purchases"), "Taxable value", say("VAT")]}
        rows={inputRows}
        loading={isPending}
      />
      <StatementTable
        caption={say("VAT payable")}
        columns={["Summary", "Amount"]}
        rows={summary}
        loading={isPending}
      />

      <p className="text-fg-muted text-xs">
        Worked out from the documents entered here. It is not a filing with the tax authority. An
        accountant should check it before anything is filed from it.
      </p>
    </ReportFrame>
  );
}
